import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  IReduxRequestNode,
  IStandardGUJRequestParams,
} from "@litres-libs/types";
import { Request } from "../../../request/Request";
import { addHydrateReducer } from "../../../utils/hydrateReducer";

interface ILoyaltyCard {
  id: string;
  cardNum?: string;
  isActive?: boolean;
}

interface ILoyaltyState {
  list: ILoyaltyCard[];
  actions: {
    changeLoyaltyCSRF: IChangeLoyaltyCSRFNode;
    getLoyaltyCardsCSRF: IGetLoyaltyCardsCSRFNode;
  };
}
type IChangeLoyaltyCSRFNode = IReduxRequestNode<
  IChangeLoyaltyCSRFRequest,
  IChangeLoyaltyGUJResponse
>;
type IGetLoyaltyCardsCSRFNode = IReduxRequestNode<
  IGetLoyaltyCardsGUJRequest,
  IGetLoyaltyCardsGUJResponse
>;

const initialState: ILoyaltyState = {
  list: [],
  actions: {
    changeLoyaltyCSRF: {},
    getLoyaltyCardsCSRF: {},
  },
};

type IGetLoyaltyCardsGUJRequest = IStandardGUJRequestParams;

interface IGetLoyaltyCardsGUJResponse {
  cards: ILoyaltyCard[];
}

export const getLoyaltyCardsCSRF = createAsyncThunk(
  "pages/personalCabinet/getLoyaltyCardsCSRF",
  async () => {
    const response = new Request.CSRF<
      IGetLoyaltyCardsGUJRequest,
      IGetLoyaltyCardsGUJResponse
    >({
      url: "/pages/personal_cabinet_loyalties/?lite=1&gu_ajax=1",
      provider: {
        id: "get_loyalty_cards",
      },
      request: {
        method: "GET",
      },
    });

    return await response.fetch();
  }
);
interface ILoyaltyCardsChangeCSRFParams {
  loyalty: string;
  code: string;
  act: string;
}
type IChangeLoyaltyCSRFRequest = IStandardGUJRequestParams &
  ILoyaltyCardsChangeCSRFParams;

interface IChangeLoyaltyGUJResponse {
  status: string;
}

export const changeLoyaltyCSRF = createAsyncThunk(
  "pages/personalCabinet/changeLoyaltyCSRF",
  async (params: {
    data: ILoyaltyCardsChangeCSRFParams;
    onSuccess?: (success: IChangeLoyaltyCSRFNode) => void;
    onError?: (error: IChangeLoyaltyCSRFNode) => void;
  }) => {
    const response = new Request.CSRF<
      IChangeLoyaltyCSRFRequest,
      IChangeLoyaltyGUJResponse
    >({
      url: "/pages/ajax_empty2/",
      provider: {
        id: "change_loyalty",
      },
      request: {
        method: "POST",
        data: {
          action: "change_loyalty",
          gu_ajax: true,
          ...params.data,
        },
      },
      onSuccess: params.onSuccess,
      onError: params.onError,
    });

    return await response.fetch();
  }
);

export const loyaltyCardsSlice = createSlice({
  name: "pages/personalCabinet/loyalty",
  initialState,
  reducers: {
    setLoyaltyCards: (
      state: ILoyaltyState,
      action: PayloadAction<ILoyaltyCard[]>
    ) => {
      state.list = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "pages.personalCabinet.loyaltyCards");
    /**
     * Обработка загрузки бонусных карт getLoyaltyCardsCSRF
     */
    builder.addCase(getLoyaltyCardsCSRF.pending, (state: ILoyaltyState) => {
      state.actions.getLoyaltyCardsCSRF.isLoading = true;
      state.actions.getLoyaltyCardsCSRF.error = undefined;
    });
    builder.addCase(
      getLoyaltyCardsCSRF.fulfilled,
      (
        state: ILoyaltyState,
        action: PayloadAction<IGetLoyaltyCardsCSRFNode>
      ) => {
        state.actions.getLoyaltyCardsCSRF = action.payload;
        state.actions.getLoyaltyCardsCSRF.isLoading = false;

        if (action.payload.response?.cards?.length) {
          state.list = action.payload.response.cards;
        }

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса addItemRatingCSRF",
            action.payload.error
          );
          state.actions.getLoyaltyCardsCSRF.error = action.payload.error;
        }
      }
    );

    /**
     * Обработка загрузки и ответа от changeLoyalty
     */
    builder.addCase(changeLoyaltyCSRF.pending, (state: ILoyaltyState) => {
      state.actions.changeLoyaltyCSRF.isLoading = true;
    });
    builder.addCase(
      changeLoyaltyCSRF.fulfilled,
      (state: ILoyaltyState, action: PayloadAction<IChangeLoyaltyCSRFNode>) => {
        state.actions.changeLoyaltyCSRF = action.payload;
        state.actions.changeLoyaltyCSRF.isLoading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса addItemRatingCSRF",
            action.payload.error
          );
          state.actions.changeLoyaltyCSRF.error = action.payload.error;
        }
      }
    );
  },
});

export const { setLoyaltyCards } = loyaltyCardsSlice.actions;

export default loyaltyCardsSlice.reducer;
